import { useState } from "react";
import AppShell from "@/components/layout/AppShell";
import useAutoRefresh from "@/hooks/useAutoRefresh";
import DataTable from "@/components/common/DataTable";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { UserPlus, Trash, Warning, PencilSimple, Key } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";

const ROLES = [
  { v: "admin",         l: "Administrator",              desc: "Akses tertinggi sistem, kelola user" },
  { v: "owner",         l: "Owner / Direksi",            desc: "Wewenang tertinggi, hapus & edit semua data" },
  { v: "admin_kantor",  l: "Administrasi Kantor",        desc: "Di bawah Owner. Edit semua data lapangan" },
  { v: "admin_pabrik",  l: "Administrasi Pabrik",        desc: "Di bawah Adm. Kantor. Input Receiving & DO" },
  { v: "lab",           l: "Petugas Laboratorium",       desc: "Input hasil lab pada receiving barang" },
  { v: "inventory",     l: "Inventory Control",          desc: "Stok opname harian, variance, tank balance" },
  { v: "security",      l: "Security + Op. Timbangan",   desc: "Gerbang masuk/keluar & timbang masuk/keluar" },
  { v: "supervisor",    l: "Supervisor Lapangan",        desc: "Bongkar/loading, verifikasi lapangan (input data)" },
  { v: "approver",      l: "Pengawas Pabrik (Approver)", desc: "HANYA approve/reject DO — tidak bisa input data" },
];

const ROLE_LABEL = Object.fromEntries(ROLES.map((r) => [r.v, r.l]));

const initial = { email: "", password: "", name: "", role: "operasional" };

export default function UserManagementPage() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(initial);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [q, setQ] = useState("");
  const [roleEdit, setRoleEdit] = useState(null);   // {id, email, role}
  const [pwReset, setPwReset]   = useState(null);   // {id, email}
  const [newPw, setNewPw]       = useState("");
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  const isOwnerLike = user && (user.role === "admin" || user.role === "owner");

  const load = () => api.get("/users").then((r) => setRows(r.data)).catch(() => {});
  useAutoRefresh(load, { enabled: !!isOwnerLike, deps: [user] });

  // guard: only admin/owner can access
  if (user && !isOwnerLike) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/auth/register", form);
      toast.success(`Akun ${form.email} berhasil dibuat`);
      setForm(initial); setOpen(false); load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Hapus akun ${row.email}?`)) return;
    try {
      await api.delete(`/users/${row.id}`);
      toast.success("Akun dihapus");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const filtered = rows.filter((r) =>
    !q || `${r.email} ${r.name} ${r.role}`.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <AppShell
      title="Manajemen User"
      subtitle="Kelola akun operator, security, supervisor, dan owner"
      actions={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="user-new-btn" className="bg-sky-600 hover:bg-sky-700 h-10 gap-2">
              <UserPlus size={16} weight="bold" /> Buat Akun Baru
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-lg">Buat Akun Pengguna Baru</DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} data-testid="user-form" className="space-y-4 mt-3">
              <Field label="Nama Lengkap" required>
                <Input data-testid="user-name" value={form.name} onChange={set("name")} required placeholder="Contoh: Budi Santoso" />
              </Field>
              <Field label="Email" required>
                <Input data-testid="user-email" type="email" value={form.email} onChange={set("email")} required placeholder="budi@primergy.com" />
              </Field>
              <Field label="Password Sementara" required>
                <Input data-testid="user-password" type="text" value={form.password} onChange={set("password")} required placeholder="Minimal 6 karakter" />
                <p className="text-[10px] text-slate-500 mt-1">Sampaikan ke user, mereka bisa ganti di profil mereka nanti.</p>
              </Field>
              <Field label="Role / Jabatan" required>
                <Select value={form.role} onValueChange={set("role")}>
                  <SelectTrigger data-testid="user-role" className="h-10"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ROLES.map((r) => (
                      <SelectItem key={r.v} value={r.v}>
                        <div className="flex flex-col">
                          <span className="font-medium">{r.l}</span>
                          <span className="text-[10px] text-slate-500">{r.desc}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-[11px] text-amber-800 flex gap-2">
                <Warning size={16} weight="fill" className="mt-0.5 shrink-0" />
                <span>Password disimpan dalam bentuk hash bcrypt — tidak bisa dilihat kembali. Pastikan Anda mencatat/menyampaikannya ke user sebelum menutup dialog ini.</span>
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Batal</Button>
                <Button type="submit" data-testid="user-submit" disabled={submitting} className="bg-slate-900 hover:bg-slate-800 text-white">
                  {submitting ? "Menyimpan…" : "Buat Akun"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      }
    >
      <div className="flex items-center gap-3 mb-5">
        <Input
          data-testid="user-search"
          placeholder="Cari nama, email, atau role…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="max-w-sm h-10"
        />
        <span className="ml-auto text-xs font-mono text-slate-500">{filtered.length} akun terdaftar</span>
      </div>

      <DataTable
        testid="user-table"
        rows={filtered}
        columns={[
          { key: "name", label: "Nama", render: (r) => (
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-full bg-slate-900 text-sky-400 flex items-center justify-center text-xs font-heading font-bold shrink-0">
                {(r.name || r.email).charAt(0).toUpperCase()}
              </div>
              <div className="leading-tight">
                <div className="font-semibold text-slate-800">{r.name || "—"}</div>
                <div className="text-[11px] text-slate-500">{r.email}</div>
              </div>
            </div>
          )},
          { key: "role", label: "Role", render: (r) => (
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 text-[11px] font-semibold rounded-full border bg-sky-50 text-sky-800 border-sky-200">
              {ROLE_LABEL[r.role] || r.role}
            </span>
          )},
          { key: "created_at", label: "Dibuat", render: (r) => (
            <span className="font-mono text-xs text-slate-500">
              {r.created_at ? new Date(r.created_at).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" }) : "-"}
            </span>
          )},
          { key: "actions", label: "", render: (r) => (
            <div className="flex items-center justify-end gap-1">
              {r.role !== "admin" && (
                <>
                  <button
                    onClick={() => setRoleEdit({ id: r.id, email: r.email, role: r.role })}
                    data-testid={`user-role-${r.email}`}
                    className="text-sky-700 hover:text-sky-900 p-1.5 hover:bg-sky-50 rounded-md transition-colors"
                    title="Ubah role"
                  >
                    <PencilSimple size={15} weight="duotone" />
                  </button>
                  <button
                    onClick={() => { setPwReset({ id: r.id, email: r.email }); setNewPw(""); }}
                    data-testid={`user-reset-${r.email}`}
                    className="text-amber-700 hover:text-amber-900 p-1.5 hover:bg-amber-50 rounded-md transition-colors"
                    title="Reset password"
                  >
                    <Key size={15} weight="duotone" />
                  </button>
                  <button
                    onClick={() => remove(r)}
                    data-testid={`user-delete-${r.email}`}
                    className="text-red-600 hover:text-red-800 p-1.5 hover:bg-red-50 rounded-md transition-colors"
                    title="Hapus akun"
                  >
                    <Trash size={15} weight="duotone" />
                  </button>
                </>
              )}
              {r.role === "admin" && (
                <span className="text-[10px] text-slate-400 uppercase tracking-widest">terkunci</span>
              )}
            </div>
          )},
        ]}
      />

      {/* Role change dialog */}
      <Dialog open={!!roleEdit} onOpenChange={(o) => !o && setRoleEdit(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg">Ubah Role — {roleEdit?.email}</DialogTitle>
          </DialogHeader>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              try {
                await api.patch(`/users/${roleEdit.id}/role`, { role: roleEdit.role });
                toast.success(`Role user diubah menjadi ${ROLE_LABEL[roleEdit.role]}`);
                setRoleEdit(null); load();
              } catch (err) {
                toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
              }
            }}
            className="space-y-4 mt-2"
            data-testid="user-role-form"
          >
            <Field label="Role Baru" required>
              <Select value={roleEdit?.role || ""} onValueChange={(v) => setRoleEdit((s) => ({ ...s, role: v }))}>
                <SelectTrigger data-testid="user-role-select" className="h-10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.filter((r) => r.v !== "admin").map((r) => (
                    <SelectItem key={r.v} value={r.v}>
                      <div className="flex flex-col">
                        <span className="font-medium">{r.l}</span>
                        <span className="text-[10px] text-slate-500">{r.desc}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="outline" onClick={() => setRoleEdit(null)}>Batal</Button>
              <Button type="submit" data-testid="user-role-submit" className="bg-slate-900 hover:bg-slate-800 text-white">Simpan</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Password reset dialog */}
      <Dialog open={!!pwReset} onOpenChange={(o) => !o && setPwReset(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg">Reset Password — {pwReset?.email}</DialogTitle>
          </DialogHeader>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              if (newPw.length < 6) return toast.error("Password minimal 6 karakter");
              try {
                await api.post(`/users/${pwReset.id}/reset-password`, { new_password: newPw });
                toast.success(`Password ${pwReset.email} berhasil di-reset. Sampaikan password baru ke user.`);
                setPwReset(null); setNewPw("");
              } catch (err) {
                toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
              }
            }}
            className="space-y-4 mt-2"
            data-testid="user-reset-form"
          >
            <Field label="Password Baru (min 6 karakter)" required>
              <Input
                data-testid="user-reset-password"
                type="text"
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                placeholder="mis. Reset1234!"
                className="font-mono"
                required
              />
              <p className="text-[10px] text-slate-500 mt-1">Password ini akan langsung aktif. Catat & sampaikan ke user; mereka bisa ganti sendiri di menu profil.</p>
            </Field>
            <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-[11px] text-amber-800 flex gap-2">
              <Warning size={16} weight="fill" className="mt-0.5 shrink-0" />
              <span>Password lama tidak bisa dilihat karena disimpan sebagai hash bcrypt (satu arah). Reset hanya menimpa dengan password baru.</span>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="outline" onClick={() => setPwReset(null)}>Batal</Button>
              <Button type="submit" data-testid="user-reset-submit" className="bg-amber-600 hover:bg-amber-700 text-white">Reset Password</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-600">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
